import React from 'react';
import {
  FiGlobe, FiMapPin, FiInstagram, FiStar, FiSearch, FiMessageCircle,
  FiFileText, FiPhoneCall, FiTarget
} from 'react-icons/fi';
import PageHeader from '../components/PageHeader';
import Reveal from '../components/Reveal';
import SectionHead from '../components/SectionHead';
import AuditForm from '../components/AuditForm';
import FAQ from '../components/FAQ';
import usePageMeta from '../utils/usePageMeta';
import JsonLd from '../components/JsonLd';
import { faqSchema } from '../utils/schema';

const checks = [
  { icon: FiGlobe, title: 'Website', text: 'Mobile experience, speed, clarity of courses and fees, and how easy it is to enquire.' },
  { icon: FiMapPin, title: 'Google Business Profile', text: 'Whether your listing is complete, accurate and showing up in local searches.' },
  { icon: FiStar, title: 'Reviews & reputation', text: 'Your rating, review count and how reviews are being answered.' },
  { icon: FiInstagram, title: 'Social media', text: 'Posting consistency, content quality and how well it builds trust with parents.' },
  { icon: FiSearch, title: 'Search visibility', text: 'Whether families searching for institutes like yours can actually find you.' },
  { icon: FiMessageCircle, title: 'Enquiry journey', text: 'What happens after someone calls, messages or fills a form, and where leads are lost.' }
];

// TODO: confirm the audit turnaround and format with the team.
const deliverables = [
  { icon: FiFileText, title: 'A clear written report', text: 'What’s working, what isn’t, and why it matters, in plain language.' },
  { icon: FiTarget, title: 'Your top priority fixes', text: 'The few changes that will make the biggest difference first.' },
  { icon: FiPhoneCall, title: 'A free call to walk through it', text: 'We explain the findings and answer your questions. No sales pressure.' }
];

const auditFaqs = [
  { question: 'Is the audit really free?', answer: 'Yes. There is no cost and no obligation. If you’d like help fixing what we find, we can talk about that, but it’s entirely your choice.' },
  { question: 'We don’t have a website yet. Can we still get an audit?', answer: 'Yes. We’ll review your Google listing, reviews and social media, and recommend what a website should do for your institute.' },
  { question: 'Do you need access to our accounts?', answer: 'No. The audit uses only what’s publicly visible, the same view parents and students have.' },
  { question: 'Who is the audit for?', answer: 'Schools, colleges, kindergartens, libraries, coaching centres and private institutes of any size.' }
];

const AuditPage = () => {
  usePageMeta('Free Digital Audit for Schools & Institutes', 'Get a free, no-obligation audit of your institute’s website, Google listing, reviews and social media, with clear priority fixes.');

  return (
    <>
      <JsonLd data={faqSchema(auditFaqs)} />
      <PageHeader
        eyebrow="Free digital audit"
        title="How does your institute look online?"
        subtitle="Parents and students judge you on Google, Instagram and your website before they ever call. We'll review all of it for free and show you exactly what to fix first."
      />

      <section className="section">
        <div className="container audit-layout">
          <div>
            <SectionHead eyebrow="What we check" title="Six things that decide whether families enquire" />
            <div className="audit-checks">
              {checks.map((check, index) => {
                const CheckIcon = check.icon;
                return (
                  <Reveal key={check.title} className="audit-check" delay={index * 0.04}>
                    <span className="service-icon"><CheckIcon aria-hidden="true" /></span>
                    <div>
                      <h3>{check.title}</h3>
                      <p>{check.text}</p>
                    </div>
                  </Reveal>
                );
              })}
            </div>
          </div>

          <div className="audit-form-wrap">
            <AuditForm />
          </div>
        </div>
      </section>

      <section className="section section-alt">
        <div className="container">
          <SectionHead eyebrow="What you get" title="Clear answers, not a sales pitch" center />
          <div className="values-grid audit-deliverables">
            {deliverables.map((item, index) => {
              const ItemIcon = item.icon;
              return (
                <Reveal key={item.title} className="value-card" delay={index * 0.06}>
                  <div className="service-icon"><ItemIcon aria-hidden="true" /></div>
                  <h3>{item.title}</h3>
                  <p>{item.text}</p>
                </Reveal>
              );
            })}
          </div>
        </div>
      </section>

      <FAQ items={auditFaqs} title="About the free audit" />
    </>
  );
};

export default AuditPage;
